import type { TurnRecord } from "./insight-proposer.js";

/**
 * Sliding buffer of recent conversation turns. The host pushes each turn as
 * it occurs; the buffer caps at `maxTurns` and discards the oldest entries
 * past the cap. Token count is estimated cheaply so the host can poll
 * `tokenCount()` to decide when InsightProposer's threshold has been met.
 *
 * Default estimator is `chars / 4` — a rough baseline that overshoots for
 * CJK content and undershoots for ASCII code. Hosts that have a real
 * tokenizer can inject one via the `estimator` option.
 *
 * The buffer is process-local and not persisted — a new session starts
 * with an empty buffer, which is the intended UX (proactive proposals are
 * about *this conversation*, not a re-ingestion of past sessions; that is
 * what `memory-extended/consolidate` is for).
 */
export class TurnBuffer {
  private readonly turns: TurnRecord[] = [];
  private readonly maxTurns: number;
  private readonly estimator: (turn: TurnRecord) => number;

  constructor(options?: {
    readonly maxTurns?: number;
    readonly estimator?: (turn: TurnRecord) => number;
  }) {
    this.maxTurns = options?.maxTurns ?? 50;
    this.estimator = options?.estimator ?? defaultTokenEstimator;
  }

  /** Append a turn, evicting the oldest entry if the buffer is full. */
  add(turn: TurnRecord): void {
    this.turns.push(turn);
    while (this.turns.length > this.maxTurns) {
      this.turns.shift();
    }
  }

  /** Return the most recent `n` turns (or fewer if the buffer has fewer). */
  recent(n: number): readonly TurnRecord[] {
    if (n >= this.turns.length) return [...this.turns];
    return this.turns.slice(-n);
  }

  /** Number of turns currently buffered. */
  get length(): number {
    return this.turns.length;
  }

  /** Estimated total token count over the entire buffer. */
  tokenCount(): number {
    let total = 0;
    for (const t of this.turns) total += this.estimator(t);
    return total;
  }

  /** Reset the buffer — typically called at session-end. */
  clear(): void {
    this.turns.length = 0;
  }
}

/**
 * Backpressure controller for ambient mid-session triggers. When the user
 * declines several proposals in a row, the controller flips into
 * `active=true` and the host should pause ambient evaluations for the
 * remainder of the session. Manual `/curate` invocations are still
 * allowed — the controller exists only to suppress unsolicited prompts.
 *
 * Reset semantics:
 *  - `recordAccept()` — any acceptance clears the streak.
 *  - `recordIgnored()` — proposals the host surfaced but the user did not
 *    explicitly accept/decline do not count toward the streak.
 *  - `reset()` — explicit reset, used by `/curate` (the user re-engaging
 *    after backpressure kicked in).
 */
export class AmbientBackpressure {
  private declineStreak = 0;
  private readonly maxStreak: number;

  constructor(options?: { readonly maxStreak?: number }) {
    this.maxStreak = options?.maxStreak ?? 3;
  }

  /** Record a user decline. Counts toward backpressure. */
  recordDecline(): void {
    this.declineStreak++;
  }

  /** Record a user acceptance. Clears the streak. */
  recordAccept(): void {
    this.declineStreak = 0;
  }

  /** Explicit reset (e.g. user runs `/curate` after backpressure activated). */
  reset(): void {
    this.declineStreak = 0;
  }

  /** True when the streak has reached the maximum and ambient should pause. */
  isActive(): boolean {
    return this.declineStreak >= this.maxStreak;
  }

  /** Current decline streak — exposed for diagnostics. */
  get streak(): number {
    return this.declineStreak;
  }
}

/**
 * Rough token estimator — ~4 characters per token (English baseline).
 * Replace with a real tokenizer when accuracy matters; for the threshold
 * gate this estimate is adequate.
 */
function defaultTokenEstimator(turn: TurnRecord): number {
  return Math.ceil(turn.content.length / 4);
}
